import {
  getDemoFingerprint,
  websiteBrowserContext,
} from '@/lib/demo-fingerprint';
import type { WebsiteReplayDirectiveType } from './session-replay.model';
import {
  getWebsitePagePath,
  WebsiteReplayChunkQueue,
} from './session-replay.util';

const REPLAY_FLUSH_BYTES = 48 * 1024;
const REPLAY_MAX_EVENTS_PER_CHUNK = 1_000;
const REPLAY_FLUSH_INTERVAL_MS = 5_000;
const REPLAY_RETRY_MS = 5_000;

export class WebsiteReplayRecorder {
  private readonly chunks = new WebsiteReplayChunkQueue(
    REPLAY_FLUSH_BYTES,
    REPLAY_MAX_EVENTS_PER_CHUNK
  );
  private stopRecorder: (() => void) | null = null;
  private startPromise: Promise<void> | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private uploading = false;
  private paused = false;
  private recorderCustomEvent:
    | ((tag: string, payload: Record<string, unknown>) => void)
    | null = null;
  private lastPagePath: string | null = null;
  private pendingResume = false;

  constructor(
    private readonly phpApiUrl: string,
    private readonly onRegionExcluded: () => void
  ) {}

  heartbeatCredentials() {
    return this.chunks.credentials();
  }

  async applyDirective(directive: WebsiteReplayDirectiveType): Promise<void> {
    if (
      !directive.enabled ||
      !directive.customer_replay_uuid ||
      !directive.customer_replay_upload_token ||
      !directive.customer_replay_next_sequence
    ) {
      this.disable();
      return;
    }

    const current = this.chunks.credentials();
    if (
      current &&
      current.customer_replay_uuid !== directive.customer_replay_uuid
    ) {
      this.stopCapture();
    }
    this.chunks.activate({
      customer_replay_uuid: directive.customer_replay_uuid,
      customer_replay_upload_token: directive.customer_replay_upload_token,
      customer_replay_next_sequence: directive.customer_replay_next_sequence,
    });
    void this.drainUploads();
    if (!this.paused) await this.startCapture();
  }

  pause(): void {
    if (this.stopRecorder) {
      this.addCustomEvent('capture-paused', {});
      this.pendingResume = true;
    }
    this.paused = true;
    this.stopCapture();
    this.flush();
  }

  resume(): void {
    this.paused = false;
    void this.drainUploads();
    void this.startCapture();
  }

  stop(): void {
    this.pause();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  disable(): void {
    this.paused = true;
    this.stopCapture();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.chunks.clear();
    this.pendingResume = false;
  }

  addCustomEvent(tag: string, payload: Record<string, unknown>): void {
    this.recorderCustomEvent?.(tag, payload);
  }

  recordPageView(pagePath: string): void {
    if (!this.recorderCustomEvent || pagePath === this.lastPagePath) return;
    this.addCustomEvent(`page: ${pagePath}`, {
      pagePath,
      url: window.location.href,
      title: document.title,
    });
    this.lastPagePath = pagePath;
  }

  flush(): void {
    this.chunks.flush();
    void this.drainUploads();
  }

  private async startCapture(): Promise<void> {
    if (this.paused || this.stopRecorder || !this.chunks.credentials()) {
      return;
    }
    if (this.startPromise) {
      await this.startPromise;
      return;
    }

    const startPromise = this.initializeRecorder();
    this.startPromise = startPromise;
    try {
      await startPromise;
    } finally {
      if (this.startPromise === startPromise) this.startPromise = null;
    }
  }

  private async initializeRecorder(): Promise<void> {
    try {
      const [{ record }, { pack }] = await Promise.all([
        import('@rrweb/record'),
        import('@rrweb/packer'),
      ]);
      if (this.paused || !this.chunks.credentials()) return;

      const stopRecorder =
        record<string>({
          emit: event => {
            if (this.chunks.add(event)) void this.drainUploads();
          },
          packFn: pack,
          blockSelector: '[data-replay-block]',
          maskTextSelector: '[data-replay-mask]',
          maskAllInputs: false,
          maskInputOptions: {},
          userTriggeredOnInput: true,
          recordAfter: 'DOMContentLoaded',
          checkoutEveryNms: 2 * 60 * 1000,
          inlineImages: false,
          recordCanvas: false,
          collectFonts: false,
          recordCrossOriginIframes: false,
          sampling: {
            mousemove: 50,
            scroll: 150,
            input: 'all',
          },
        }) ?? null;
      if (!stopRecorder) throw new Error('rrweb recorder did not start');
      if (this.paused) {
        stopRecorder();
        return;
      }
      this.stopRecorder = stopRecorder;
      this.recorderCustomEvent = (tag, payload) =>
        record.addCustomEvent(tag, payload);
      if (this.pendingResume) {
        this.addCustomEvent('capture-resumed', {});
        this.pendingResume = false;
      }
      this.recordPageView(getWebsitePagePath());
      this.flushTimer = setInterval(
        () => this.flush(),
        REPLAY_FLUSH_INTERVAL_MS
      );
    } catch (error) {
      console.warn('[WebsiteSessionReplay] Recorder failed to start', error);
      this.scheduleRetry();
    }
  }

  private stopCapture(): void {
    this.stopRecorder?.();
    this.stopRecorder = null;
    this.recorderCustomEvent = null;
    this.lastPagePath = null;
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flushTimer = null;
  }

  private async drainUploads(): Promise<void> {
    if (this.uploading) return;
    this.uploading = true;
    try {
      let chunk = this.chunks.firstPending();
      while (chunk) {
        // Upload with the CURRENT token when it belongs to the SAME replay. A
        // heartbeat from another tab of the same session rotates the token, and
        // a chunk queued before that carries the old one — sending it as queued
        // can never succeed. A chunk left over from a previous replay uuid must
        // keep its own token, or the uuid/token pair would not match.
        const credentials = this.chunks.credentials();
        if (!credentials) return;
        const body =
          credentials.customer_replay_uuid === chunk.customer_replay_uuid
            ? {
                ...chunk,
                customer_replay_upload_token:
                  credentials.customer_replay_upload_token,
              }
            : chunk;
        const response = await fetch(
          `${this.phpApiUrl.replace(/\/$/, '')}/v1/external/website-replay-chunk`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-OB-Context': websiteBrowserContext(getDemoFingerprint()),
            },
            body: JSON.stringify(body),
            keepalive: this.paused,
          }
        );
        if (response.status >= 400 && response.status < 500) {
          // The server rejected this recording (rotated token, expired window,
          // sequence gap). Retrying the same chunk is pointless: drop the
          // queue and its credentials and let the next heartbeat directive
          // hand out fresh ones.
          console.warn(
            `[WebsiteSessionReplay] Chunk rejected (HTTP ${response.status}), waiting for a new directive`
          );
          this.chunks.clear();
          this.stopCapture();
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = (await response.json()) as { enabled: boolean };
        if (result.enabled !== true) {
          this.disable();
          this.onRegionExcluded();
          return;
        }
        this.chunks.acknowledge(chunk);
        chunk = this.chunks.firstPending();
      }
    } catch (error) {
      console.warn('[WebsiteSessionReplay] Chunk upload failed', error);
      this.scheduleRetry();
    } finally {
      this.uploading = false;
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer || this.paused) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.drainUploads();
      void this.startCapture();
    }, REPLAY_RETRY_MS);
  }
}
